import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listBillingProfiles,
  upsertBillingProfile,
  deleteBillingProfile,
  setDefaultBillingProfile,
  verifyVatWithVies,
  listMyInvoices,
  getInvoiceDownloadUrl,
} from "@/lib/account/billing.functions";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CountryCombobox } from "@/components/account/CountryCombobox";
import { EmptyState } from "@/components/account/EmptyState";
import { COUNTRY_MAP, isEuCountry, vatPrefixFor } from "@/lib/countries";
import { toast } from "sonner";
import {
  Building2, User, ReceiptText, Check, Loader2, ShieldCheck, ShieldAlert,
  Star, Pencil, Trash2, Plus, Download, FileText, ChevronRight, Info,
} from "lucide-react";

export const Route = createFileRoute("/_account/account/billing")({
  head: () => ({ meta: [{ title: "Billing details · FIXHEN" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: BillingPage,
});

type Persona = "individual" | "business";
type BillingProfile = {
  id?: string;
  label?: string | null;
  persona: Persona;
  legal_name: string;
  contact_name?: string | null;
  email: string;
  phone?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  region?: string | null;
  postal_code: string;
  country_code: string;
  vat_number?: string | null;
  vat_valid?: boolean | null;
  vat_validated_at?: string | null;
  vies_name?: string | null;
  vies_address?: string | null;
  eori_number?: string | null;
  tax_reference?: string | null;
  invoice_language: string;
  currency: string;
  po_reference_default?: string | null;
  payment_terms_days: number;
  is_default: boolean;
  notes?: string | null;
};

const empty: BillingProfile = {
  persona: "individual",
  legal_name: "",
  email: "",
  line1: "",
  city: "",
  postal_code: "",
  country_code: "BG",
  invoice_language: "en",
  currency: "EUR",
  payment_terms_days: 14,
  is_default: false,
};

function BillingPage() {
  const qc = useQueryClient();
  const listBP = useServerFn(listBillingProfiles);
  const listInv = useServerFn(listMyInvoices);
  const saveFn = useServerFn(upsertBillingProfile);
  const delFn = useServerFn(deleteBillingProfile);
  const defFn = useServerFn(setDefaultBillingProfile);

  const bpQ = useQuery({ queryKey: ["account", "billing", "profiles"], queryFn: () => listBP() });
  const invQ = useQuery({ queryKey: ["account", "billing", "invoices"], queryFn: () => listInv() });

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<BillingProfile>(empty);
  const [confirmDelete, setConfirmDelete] = useState<BillingProfile | null>(null);

  const saveM = useMutation({
    mutationFn: (d: BillingProfile) => saveFn({ data: d as any }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", "billing"] });
      toast.success("Billing profile saved");
      setOpen(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });
  const delM = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", "billing"] });
      toast.success("Billing profile deleted");
      setConfirmDelete(null);
    },
  });
  const defM = useMutation({
    mutationFn: (id: string) => defFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", "billing"] });
      toast.success("Default updated");
    },
  });

  const profiles = (bpQ.data ?? []) as BillingProfile[];
  const invoices = invQ.data ?? [];

  function openEdit(p?: BillingProfile) {
    setDraft(p ? { ...p } : { ...empty, is_default: profiles.length === 0 });
    setOpen(true);
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Billing details</h1>
          <p className="text-sm text-slate-500">
            Save the legal identity used on your invoices. VAT-registered businesses in the EU get
            automatic reverse-charge handling once your VAT ID is verified with VIES.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openEdit()} className="bg-[#2563EB] hover:bg-[#1d4ed8]">
              <Plus className="mr-1.5 h-4 w-4" /> Add billing profile
            </Button>
          </DialogTrigger>
          <ProfileDialog draft={draft} setDraft={setDraft} onSubmit={() => saveM.mutate(draft)} pending={saveM.isPending} />
        </Dialog>
      </header>

      {bpQ.isLoading ? (
        <div className="rounded-2xl border bg-white p-10 text-center text-sm text-slate-500">Loading…</div>
      ) : profiles.length === 0 ? (
        <EmptyState
          icon={ReceiptText}
          title="No billing profile yet"
          description="Add one to receive professional invoices with reverse-charge, OSS and export handling."
          actionLabel="Add billing profile"
          onAction={() => openEdit()}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {profiles.map((p) => <ProfileCard key={p.id} p={p} onEdit={() => openEdit(p)} onDelete={() => setConfirmDelete(p)} onSetDefault={() => defM.mutate(p.id!)} />)}
        </div>
      )}

      {/* Invoices */}
      <section className="rounded-2xl border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-100 p-5">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Your invoices</h2>
            <p className="text-xs text-slate-500">Immutable records — corrections issued as credit notes.</p>
          </div>
          <ReceiptText className="h-5 w-5 text-slate-400" />
        </header>
        {invQ.isLoading ? (
          <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
        ) : invoices.length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-900">No invoices yet</p>
            <p className="text-sm text-slate-500">Invoices appear here once your orders are paid.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invoices.map((i: any) => <InvoiceRow key={i.id} inv={i} />)}
          </ul>
        )}
      </section>

      <p className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
        <Info className="mr-1 inline h-3.5 w-3.5 -mt-0.5 text-slate-400" />
        Invoices comply with EN 16931 (EU e-invoice semantic model). Reverse-charge notice is added
        automatically to EU B2B invoices with a valid VAT ID (VIES-verified). Non-EU shipments are
        zero-rated as exports. Domestic invoices apply {20}% Bulgarian VAT.
      </p>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this billing profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This will not affect invoices already issued to it — those remain immutable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete?.id && delM.mutate(confirmDelete.id)}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProfileCard({
  p, onEdit, onDelete, onSetDefault,
}: { p: BillingProfile; onEdit: () => void; onDelete: () => void; onSetDefault: () => void }) {
  const country = COUNTRY_MAP[p.country_code];
  const PIcon = p.persona === "business" ? Building2 : User;
  return (
    <div className={`relative rounded-2xl border ${p.is_default ? "border-[#2563EB] ring-2 ring-[#2563EB]/20" : "border-slate-200"} bg-white p-5`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {p.label || (p.persona === "business" ? "Business" : "Individual")}
          </p>
          <div className="mt-1 flex items-center gap-2">
            <PIcon className="h-4 w-4 text-slate-400" />
            <p className="truncate text-base font-semibold text-slate-900">{p.legal_name}</p>
          </div>
          {p.contact_name && <p className="text-sm text-slate-600">{p.contact_name}</p>}
          <p className="mt-2 text-sm text-slate-700">{p.line1}{p.line2 ? `, ${p.line2}` : ""}</p>
          <p className="text-sm text-slate-700">
            {p.postal_code} {p.city}{p.region ? `, ${p.region}` : ""} · {country?.flag} {country?.name ?? p.country_code}
          </p>
          <p className="mt-1 text-xs text-slate-500">{p.email}</p>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900" aria-label="Edit">
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={onDelete} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-700" aria-label="Delete">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {p.is_default && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#2563EB]/10 px-2 py-0.5 text-[11px] font-semibold text-[#2563EB]">
            <Star className="h-3 w-3" /> Default
          </span>
        )}
        {p.vat_number ? (
          p.vat_valid ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
              <ShieldCheck className="h-3 w-3" /> VAT verified · {p.vat_number}
            </span>
          ) : p.vat_valid === false ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
              <ShieldAlert className="h-3 w-3" /> VAT rejected · {p.vat_number}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
              <ShieldAlert className="h-3 w-3" /> VAT unverified · {p.vat_number}
            </span>
          )
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
            No VAT ID
          </span>
        )}
        {p.eori_number && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">EORI</span>
        )}
      </div>

      {!p.is_default && (
        <button onClick={onSetDefault} className="mt-3 text-xs font-semibold text-[#2563EB] hover:underline">
          Set as default →
        </button>
      )}
    </div>
  );
}

function InvoiceRow({ inv }: { inv: any }) {
  const getUrl = useServerFn(getInvoiceDownloadUrl);
  const [busy, setBusy] = useState(false);
  async function download() {
    try {
      setBusy(true);
      const r = await getUrl({ data: { invoice_id: inv.id } });
      const a = document.createElement("a");
      a.href = r.url;
      a.download = r.filename;
      a.click();
    } catch (e: any) {
      toast.error(e?.message ?? "Unable to download");
    } finally { setBusy(false); }
  }
  const schemeMeta: Record<string, { label: string; tone: string }> = {
    domestic: { label: "Domestic", tone: "bg-sky-100 text-sky-800" },
    eu_b2b_reverse_charge: { label: "Reverse charge", tone: "bg-emerald-100 text-emerald-800" },
    eu_b2c: { label: "EU B2C · OSS", tone: "bg-violet-100 text-violet-800" },
    export: { label: "Export", tone: "bg-amber-100 text-amber-800" },
    none: { label: "—", tone: "bg-slate-100 text-slate-700" },
  };
  const m = schemeMeta[inv.scheme] ?? schemeMeta.none;
  return (
    <li className="flex items-center gap-4 p-4">
      <div className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-500">
        <FileText className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{inv.invoice_number}</p>
        <p className="text-xs text-slate-500">
          Order #{inv.orders?.order_number ?? "—"} · {new Date(inv.issued_at).toLocaleDateString()}
        </p>
      </div>
      <span className={`hidden sm:inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.tone}`}>{m.label}</span>
      <div className="text-right">
        <p className="text-sm font-bold text-slate-900">{(inv.total_cents / 100).toFixed(2)} {inv.currency}</p>
      </div>
      <button
        onClick={download}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
      </button>
    </li>
  );
}

function ProfileDialog({
  draft, setDraft, onSubmit, pending,
}: { draft: BillingProfile; setDraft: (d: BillingProfile) => void; onSubmit: () => void; pending: boolean }) {
  const verify = useServerFn(verifyVatWithVies);
  const [busy, setBusy] = useState(false);
  const buyerEu = isEuCountry(draft.country_code);
  const needsEori = !buyerEu && draft.persona === "business";

  useEffect(() => {
    // When country changes and VAT is empty, suggest the prefix.
    if (!draft.vat_number && draft.country_code && draft.persona === "business") {
      const prefix = vatPrefixFor(draft.country_code);
      if (prefix) setDraft({ ...draft, vat_number: prefix });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.country_code, draft.persona]);

  async function runVerify() {
    if (!draft.vat_number) return toast.error("Enter a VAT number first");
    setBusy(true);
    try {
      const r = await verify({ data: { vat_number: draft.vat_number, country_code: draft.country_code, id: draft.id } });
      if (r.valid) {
        toast.success("VAT number is valid (VIES)");
        const patch: Partial<BillingProfile> = { vat_valid: true, vat_validated_at: new Date().toISOString(), vies_name: r.name, vies_address: r.address };
        // Offer autofill
        if (r.name && !draft.legal_name) patch.legal_name = r.name;
        setDraft({ ...draft, ...patch } as BillingProfile);
      } else {
        setDraft({ ...draft, vat_valid: false });
        toast.error(r.raw_error === "vies_timeout" || r.raw_error === "vies_unreachable"
          ? "VIES service unavailable — try again shortly"
          : "VAT number not valid");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally { setBusy(false); }
  }

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{draft.id ? "Edit billing profile" : "Add billing profile"}</DialogTitle>
      </DialogHeader>

      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
        {/* Persona segmented */}
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
          {(["individual", "business"] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setDraft({ ...draft, persona: p })}
              className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition ${draft.persona === p ? "bg-white text-[#172554] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
            >
              {p === "business" ? <Building2 className="h-4 w-4" /> : <User className="h-4 w-4" />}
              {p === "business" ? "Business" : "Individual"}
            </button>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Profile label (optional)">
            <Input value={draft.label ?? ""} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Personal, My GmbH…" />
          </Field>
          <Field label={draft.persona === "business" ? "Legal company name" : "Full name"} required>
            <Input required value={draft.legal_name} onChange={(e) => setDraft({ ...draft, legal_name: e.target.value })} />
          </Field>
        </div>

        {draft.persona === "business" && (
          <Field label="Contact person">
            <Input value={draft.contact_name ?? ""} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} />
          </Field>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Invoice email" required>
            <Input type="email" required value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </Field>
          <Field label="Phone">
            <Input value={draft.phone ?? ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
          </Field>
        </div>

        <Field label="Address line 1" required>
          <Input required value={draft.line1} onChange={(e) => setDraft({ ...draft, line1: e.target.value })} />
        </Field>
        <Field label="Address line 2">
          <Input value={draft.line2 ?? ""} onChange={(e) => setDraft({ ...draft, line2: e.target.value })} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Label className="mb-1 block text-xs font-medium text-slate-700">City *</Label>
            <Input required value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
          </div>
          <Field label="Region / State">
            <Input value={draft.region ?? ""} onChange={(e) => setDraft({ ...draft, region: e.target.value })} />
          </Field>
          <Field label="Postal code" required>
            <Input required value={draft.postal_code} onChange={(e) => setDraft({ ...draft, postal_code: e.target.value })} />
          </Field>
        </div>
        <Field label="Country" required>
          <CountryCombobox value={draft.country_code} onChange={(c) => setDraft({ ...draft, country_code: c })} />
        </Field>

        {/* Tax block */}
        {draft.persona === "business" && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tax identification</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
              <Field label={buyerEu ? "VAT ID (VIES)" : "Tax ID / VAT ID"}>
                <Input
                  value={draft.vat_number ?? ""}
                  onChange={(e) => setDraft({ ...draft, vat_number: e.target.value.toUpperCase(), vat_valid: null })}
                  placeholder={buyerEu ? "e.g. DE123456789" : "Local tax id"}
                />
              </Field>
              {buyerEu && (
                <div className="flex items-end">
                  <Button type="button" onClick={runVerify} disabled={busy || !draft.vat_number} className="bg-[#172554] hover:bg-[#0f1a3f]">
                    {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />}
                    Verify with VIES
                  </Button>
                </div>
              )}
            </div>
            {draft.vies_name && draft.vat_valid && (
              <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                <p className="font-semibold">Registered as</p>
                <p>{draft.vies_name}</p>
                {draft.vies_address && <p className="mt-1 whitespace-pre-line text-emerald-800/80">{draft.vies_address}</p>}
                {draft.vat_validated_at && (
                  <p className="mt-1 text-[11px] text-emerald-700">Verified {new Date(draft.vat_validated_at).toLocaleString()}</p>
                )}
              </div>
            )}
            {needsEori && (
              <div className="mt-3">
                <Field label="EORI number (customs, non-EU)">
                  <Input value={draft.eori_number ?? ""} onChange={(e) => setDraft({ ...draft, eori_number: e.target.value })} />
                </Field>
              </div>
            )}
          </div>
        )}

        {/* Invoicing preferences */}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Invoice language">
            <select
              value={draft.invoice_language}
              onChange={(e) => setDraft({ ...draft, invoice_language: e.target.value })}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
            >
              <option value="en">English</option>
              <option value="bg">Български</option>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
              <option value="it">Italiano</option>
              <option value="es">Español</option>
            </select>
          </Field>
          <Field label="Currency">
            <select
              value={draft.currency}
              onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
            >
              {["EUR","BGN","USD","GBP","CHF","SEK","PLN"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Payment terms (days)">
            <Input
              type="number" min={0} max={180}
              value={draft.payment_terms_days}
              onChange={(e) => setDraft({ ...draft, payment_terms_days: Number(e.target.value) })}
            />
          </Field>
        </div>
        <Field label="Default PO reference (optional)">
          <Input value={draft.po_reference_default ?? ""} onChange={(e) => setDraft({ ...draft, po_reference_default: e.target.value })} />
        </Field>
        <Field label="Internal notes (optional)">
          <Textarea rows={2} value={draft.notes ?? ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
        </Field>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={draft.is_default}
            onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-[#2563EB]"
          />
          Use as default for new orders
        </label>

        <DialogFooter>
          <Button type="submit" disabled={pending} className="bg-[#2563EB] hover:bg-[#1d4ed8]">
            {pending ? "Saving…" : "Save billing profile"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <Label className="mb-1 block text-xs font-medium text-slate-700">
        {label}{required && <span className="text-rose-600"> *</span>}
      </Label>
      {children}
    </div>
  );
}